import type { VoidFunctionComponent } from 'react';

import { LAST_ANALYZED_SORT, TITLE_SORT } from '../../../../../../../../../shared/projectsV2';
import DropdownButton from '../../../../../../../generic/DropdownButton';
import Icon, { icons } from '../../../../../../../generic/Icon';
import type { ReleaseGroupState } from '../../../../hooks/useReleaseGroupState';
import type { ReleaseGroupProjectSort } from '../../../types';

import styles from './ProjectSortingDropdown.module.less';

type Props = Pick<ReleaseGroupState, 'sort' | 'setSort'>;

const { ASC: titleAsc, DESC: titleDesc } = TITLE_SORT;
const { ASC: lastAnalyzedAsc, DESC: lastAnalyzedDesc } = LAST_ANALYZED_SORT;

const sortToDropdownLabelMap: Record<ReleaseGroupProjectSort, string> = {
  [titleAsc]: 'Sort by Title',
  [titleDesc]: 'Sort by Title',
  [lastAnalyzedAsc]: 'Sort by Last Analyzed Revision',
  [lastAnalyzedDesc]: 'Sort by Last Analyzed Revision',
};

//  Helper for determining if the title sort is active.
const isTitleSortActive = (sort: ReleaseGroupProjectSort) =>
  [titleAsc, titleDesc].includes(sort as ('title_asc' | 'title_desc'));

//  Helper for determining if the last analyzed sort is active.
const isLastAnalyzedSortActive = (sort: ReleaseGroupProjectSort) =>
  [lastAnalyzedAsc, lastAnalyzedDesc].includes(sort as ('last-analyzed_asc' | 'last-analyzed_desc'));

//  Basic check for if the sort is ascending.
const isSortAsc = (sort: ReleaseGroupProjectSort) =>
  sort.toLowerCase().endsWith('asc');

/**
 * ProjectSortingDropdown renders a dropdown button which allows you
 * to switch between different sorting options for the projects table.
 */
const ProjectSortingDropdown: VoidFunctionComponent<Props> = ({ sort, setSort }) => {
  const isActive = {
    title: isTitleSortActive(sort),
    lastAnalyzed: isLastAnalyzedSortActive(sort),
  };

  const onTitleSort = () => setSort(prevSort => {
    if (isLastAnalyzedSortActive(prevSort)) {
      return titleDesc as ReleaseGroupProjectSort;
    }
    return (isSortAsc(prevSort) ? titleDesc : titleAsc) as ReleaseGroupProjectSort;
  });

  const onLastAnalyzedSort = () => setSort(prevSort => {
    if (isTitleSortActive(prevSort)) {
      return lastAnalyzedAsc as ReleaseGroupProjectSort;
    }
    return (isSortAsc(prevSort) ? lastAnalyzedDesc : lastAnalyzedAsc) as ReleaseGroupProjectSort;
  });

  const icon = isSortAsc(sort) ? icons.angleUp : icons.angleDown;

  return (
    <DropdownButton
      id="release-group-projects-table-sorting"
      title={
        <div className={styles.sortText}>
          <span>{sortToDropdownLabelMap[sort]}</span>
          <Icon icon={isSortAsc(sort) ? icons.angleUp : icons.angleDown} />
        </div>
      }
      size="large"
      noCaret
    >
      <DropdownButton.Item active={isActive.title} onSelect={onTitleSort}>
        <span>Title</span>
        {isActive.title && <Icon icon={icon} />}
      </DropdownButton.Item>
      <DropdownButton.Item active={isActive.lastAnalyzed} onSelect={onLastAnalyzedSort}>
        <span>Last Analyzed Revision</span>
        {isActive.lastAnalyzed && <Icon icon={icon} />}
      </DropdownButton.Item>
    </DropdownButton>
  );
};

export default ProjectSortingDropdown;
